import { FC, ReactNode } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  Alert,
  Column,
  FormField,
  NumberInput,
  Radio,
  RadioGroup,
  Row,
  Select,
} from "@hightouch/htui";

import { Card } from "src/components/card";
import {
  DEFAULT_EXPIRE_AFTER,
  type ExpireAfter,
  formatExpireAfter,
  WORKSPACE_EXPIRY_DEFAULT,
} from "src/components/journey/expire-after";
import { ExpireAfterInput } from "src/components/journey/expire-after-input";
import { useUser } from "src/contexts/user-context";
import { TIME_OPTIONS } from "src/constants";
import { JourneyNodeSyncMode } from "src/types/journeys";
import { IntervalUnit } from "src/types/visual";
import { useFormkitContext } from "src/formkit/components/formkit-context";
import { OverrideConfig } from "src/components/destinations/types";
import { PageSpinner } from "src/components/loading";
import isEmpty from "lodash/isEmpty";

const getFieldName = (namePrefix: string, fieldName: string) => {
  return `${namePrefix ? namePrefix + "." : ""}${fieldName}`;
};

interface JourneySyncConfigProps {
  /** Field name prefix for form fields */
  namePrefix?: string;
  /** Whether the form fields should be disabled */
  isDisabled?: boolean;
  /** Whether the form is loading underlying data */
  isLoading?: boolean;
  /** Whether to show the journey exit warning for certain conditions */
  isJourneyExitWarningEligible?: boolean;
  /** Display mode: 'card' for Card wrapper styling, 'inline' for custom column styling */
  variant?: "card" | "inline";
  /** Right-aligned content for inline variant header (e.g., clearable/lock buttons) */
  rightContent?: ReactNode;
  /** Sync template configuration for field visibility filtering */
  syncTemplate?: {
    id?: number;
    config?: Record<string, unknown>;
    override_config?: OverrideConfig;
    journey_settings?: Record<string, unknown>;
  } | null;
}

export const JourneySyncConfig: FC<JourneySyncConfigProps> = ({
  namePrefix = "",
  isDisabled = false,
  isLoading = false,
  isJourneyExitWarningEligible = false,
  variant = "card",
  rightContent,
  syncTemplate,
}) => {
  const form = useFormContext();
  const { journeySyncExpiryEnabled } = useFlags();
  const { workspace } = useUser();
  const workspaceDefault =
    (workspace?.journey_sync_expiry_default as ExpireAfter | null) ??
    WORKSPACE_EXPIRY_DEFAULT;
  const formkitContext = useFormkitContext();
  const journeySettings = formkitContext?.journeySettings;

  // Get current values, with fallback to journey settings from template
  // Only use template fallback if form value is undefined (not set), not if it's null (explicitly cleared)
  const formMode = form.watch(getFieldName(namePrefix, "mode"));
  const currentMode = formMode !== undefined ? formMode : journeySettings?.mode;

  const formExitConfig = form.watch(getFieldName(namePrefix, "exit_config"));
  const currentExitConfig =
    formExitConfig !== undefined
      ? formExitConfig
      : journeySettings?.exit_config;

  const isCohortSync = currentMode === JourneyNodeSyncMode.Cohort;
  const isTriggerSync = currentMode === JourneyNodeSyncMode.Trigger;
  const showExpireAfterConfig = isTriggerSync && journeySyncExpiryEnabled;

  const isExplicitlyUnlocked =
    syncTemplate?.override_config?.journey_settings?.overridable === true;
  const templateHasJourneySettings =
    syncTemplate && !isEmpty(syncTemplate.journey_settings);
  // Journey settings should always be visible, but locked/disabled when the
  // template has values and not marked as overridable
  const isFieldDisabledByTemplate = Boolean(
    !isExplicitlyUnlocked && templateHasJourneySettings,
  );

  const handleModeChange = (value: JourneyNodeSyncMode) => {
    form.setValue(getFieldName(namePrefix, "mode"), value);

    form.setValue(
      getFieldName(namePrefix, "exit_config"),
      {
        remove_after: null,
        remove_on_journey_exit:
          value === JourneyNodeSyncMode.Trigger ? null : false,
        expire_after: null,
      },
      { shouldDirty: true },
    );
  };

  const renderModeSelection = () => (
    <Controller
      control={form.control}
      name={getFieldName(namePrefix, "mode")}
      defaultValue={currentMode}
      render={({ field }) => (
        <FormField label="What type of sync is this?">
          <RadioGroup
            isDisabled={isDisabled || isFieldDisabledByTemplate}
            orientation="vertical"
            value={field.value}
            onChange={(value) => {
              field.onChange(value);
              handleModeChange(value);
            }}
          >
            <Radio
              value={JourneyNodeSyncMode.Trigger}
              label="Trigger"
              description="For triggering campaigns or sending user events. The same user can be synced multiple times and a removal is not necessary."
            />
            <Radio
              value={JourneyNodeSyncMode.Cohort}
              label="Cohort"
              description="For syncing users into a cohort or list. Users will never appear in the destination cohort more than one time and you can specify when the user should be removed."
            />
          </RadioGroup>
        </FormField>
      )}
    />
  );

  const renderRemoveAfterConfig = () => (
    <Controller
      control={form.control}
      name={getFieldName(namePrefix, "exit_config.remove_after")}
      defaultValue={currentExitConfig?.remove_after}
      render={({ field }) => (
        <FormField label="Should users be removed from this sync after a specified time period?">
          <RadioGroup
            isDisabled={isDisabled || isFieldDisabledByTemplate}
            orientation="vertical"
            value={field.value !== null}
            onChange={(value) => {
              field.onChange(
                value ? { quantity: 1, unit: IntervalUnit.Day } : null,
              );
            }}
          >
            <Radio value={false} label="No, don't remove" />
            <Row align="center" gap={2}>
              <Radio value={true} label="Yes, remove after" />
              {field.value && (
                <>
                  <NumberInput
                    isDisabled={isDisabled || isFieldDisabledByTemplate}
                    placeholder="Select quantity..."
                    value={field.value?.quantity}
                    width="auto"
                    onChange={(value) =>
                      field.onChange({
                        ...field.value,
                        quantity: value,
                      })
                    }
                  />
                  <Select
                    isDisabled={isDisabled || isFieldDisabledByTemplate}
                    placeholder="Select delay unit..."
                    options={TIME_OPTIONS}
                    value={field.value?.unit}
                    width="auto"
                    onChange={(value) =>
                      field.onChange({ ...field.value, unit: value })
                    }
                  />
                </>
              )}
            </Row>
          </RadioGroup>
        </FormField>
      )}
    />
  );

  const renderExitConfig = () => (
    <Controller
      control={form.control}
      name={getFieldName(namePrefix, "exit_config.remove_on_journey_exit")}
      defaultValue={currentExitConfig?.remove_on_journey_exit || false}
      render={({ field }) => (
        <>
          <FormField label="Should users be removed from this sync when they exit the journey?">
            <RadioGroup
              isDisabled={isDisabled || isFieldDisabledByTemplate}
              orientation="vertical"
              value={field.value == true}
              onChange={(value) => {
                field.onChange(value || null);
              }}
            >
              <Radio value={false} label="No, don't remove" />
              <Radio value={true} label="Yes, remove upon exit" />
            </RadioGroup>
          </FormField>

          {isJourneyExitWarningEligible && field.value && (
            <Alert
              type="warning"
              title="Users might never be synced"
              message="After this tile, there's at least one path to completing the journey that has no time delays. Users who go down this path will immediately exit the journey after advancing from this tile, and thus be removed from the sync before it ever has a chance to run. You may want to change your selection from 'Yes, remove upon exit' or switch to a 'Trigger' sync."
            />
          )}
        </>
      )}
    />
  );

  const renderExpireAfterConfig = () => (
    <Controller
      control={form.control}
      name={getFieldName(namePrefix, "exit_config.expire_after")}
      defaultValue={currentExitConfig?.expire_after ?? null}
      render={({ field }) => (
        <FormField
          label="Entry expiry"
          description="Entries waiting longer than this window are skipped instead of sent."
        >
          <RadioGroup
            isDisabled={isDisabled || isFieldDisabledByTemplate}
            orientation="vertical"
            value={field.value != null}
            onChange={(value) => {
              field.onChange(value ? { ...DEFAULT_EXPIRE_AFTER } : null);
            }}
          >
            <Radio
              value={false}
              label={`Use the workspace default (${formatExpireAfter(workspaceDefault)})`}
            />
            <Row align="center" gap={2}>
              <Radio value={true} label="Override for this sync" />
              {field.value && (
                <ExpireAfterInput
                  isDisabled={isDisabled || isFieldDisabledByTemplate}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            </Row>
          </RadioGroup>
        </FormField>
      )}
    />
  );

  if (isLoading) {
    return <PageSpinner />;
  }

  if (variant === "card") {
    // "Card" variant - used within the journeys canvas
    return (
      <Column gap={6}>
        <Card>{renderModeSelection()}</Card>
        {isCohortSync && (
          <>
            <Card>{renderRemoveAfterConfig()}</Card>
            <Card gap={4}>{renderExitConfig()}</Card>
          </>
        )}
        {showExpireAfterConfig && <Card>{renderExpireAfterConfig()}</Card>}
      </Column>
    );
  }

  // Inline mode - similar to the original journey-settings styling
  return (
    <Column
      gap={6}
      backgroundColor="white"
      borderWidth="hairline"
      borderStyle="solid"
      borderColor="base.border"
      borderRadius="md"
      p={6}
    >
      {rightContent ? (
        <Row justify="space-between" align="flex-start">
          {renderModeSelection()}
          {rightContent}
        </Row>
      ) : (
        renderModeSelection()
      )}
      {isCohortSync && renderRemoveAfterConfig()}
      {isCohortSync && renderExitConfig()}
      {showExpireAfterConfig && renderExpireAfterConfig()}
    </Column>
  );
};
